import type { Bundle, BundleKey } from './types';
import { flatten } from './beats';
import { TRIGGER_HEALTH_TARGETS } from './types';

/**
 * WIND (ADR 0073, docs/architecture/wind-contract.md §3 and §3.8): a word-for-word TS port of shared-cs
 * `ContentValidator.Wind.cs` (the expand step). A bundle with `raidRules.wind` is a **wind bundle**: every new field is required and
 * range-checked, every tier id names a `winds[]` row, and an old HP field still in use is a **warning** (`windWarnings`), never an
 * error, for one content version. A bundle without the block (published before it) is checked only where it sets a new field anyway.
 * Parity: `validate.wind.test.ts` against `fixtures/wind-parity.json` (dumped by packages/shared-cs/Tests~/WindAdmParityDump.cs).
 *
 * The content types for the new fields are local (below) until `@argzombie/api-client` is regenerated (W2); the defaults are the
 * C# property defaults, so an absent field reads as the server reads it.
 */

type Add = (section: BundleKey, index: number | undefined, path: string, message: string) => void;

/** A located wind error (review fix: every error lands on its own section and row, as `windWarnings` does). */
type Issue = { section: BundleKey; index: number | undefined; path: string; message: string };

/** Collects errors at a location; `at` gives a sink for another row. Errors keep the C# order (parity). */
class Sink {
  constructor(
    private readonly out: Issue[],
    private readonly section: BundleKey,
    private readonly index: number | undefined,
    private readonly path: string,
  ) {}

  push(message: string) {
    this.out.push({ section: this.section, index: this.index, path: this.path, message });
  }

  at(section: string, index: number | undefined, path: string): Sink {
    return new Sink(this.out, section as BundleKey, index, path);
  }
}

/** The `winds[]` row (shared-cs `WindTierDef`). */
export type WindTierDef = {
  id?: string | null;
  name?: string | null;
  capacityMult?: number;
  recoveryMult?: number;
  biteOddsMult?: number;
  climbBackCalmS?: number;
  swingIntervalMult?: number;
  moveSpeedMult?: number;
  acquireMult?: number;
  spreadMult?: number;
  colorSlot?: string | null;
};

/** `raidRules.wind` (shared-cs `WindDef`); nested blocks may be explicitly null (an error), absent = the defaults. */
export type WindDef = {
  capacity?: number;
  drop?: { graceS?: number; landFrac?: number } | null;
  recovery?: {
    pauseS?: number;
    pauseAfterDropS?: number;
    fastPerS?: number;
    fastUntilFrac?: number;
    slowPerS?: number;
    walkMult?: number;
    crouchMult?: number;
    holdUnderThreatMult?: number;
    threatGate?: boolean;
  } | null;
  sprint?: { costPerS?: number; minToStart?: number } | null;
  costs?: {
    pushOff?: number;
    takedown?: number;
    kick?: number;
    throw?: number;
    reload?: number;
    sustainedFireWindowS?: number;
  } | null;
  bite?: { baseOdds?: number; spentK?: number; maxOdds?: number } | null;
  hits?: { damageToWind?: number } | null;
  wounds?: { toDie?: number; toDown?: number; capacityMultByWounds?: number[] | null } | null;
  rest?: {
    hoursPerWind?: { home?: number; outpost?: number; away?: number } | null;
    debtPerWound?: number;
    maxDebt?: number;
    startFloorTier?: string | null;
  } | null;
  startTier?: { v4ResumeTier?: string | null } | null;
};

/** shared-cs `WindTierIds` and `WindRules.DefaultTiers` (the ids a bundle with no `winds[]` reads). */
export const WIND_TIER_IDS = ['fresh', 'winded', 'gassed'] as const;
/** shared-cs `ZombieAttackKinds.All`. */
export const ZOMBIE_ATTACK_KINDS = ['grab', 'hit', 'mixed'] as const;
/** `ContentValidator.MinGrantWind` / `MaxGrantWind`: a `grant_wind` trigger removes 0.5..3 winds of rest debt. */
export const TRIGGER_WIND_LIMITS = { min: 0.5, max: 3 } as const;

/** C# `Num`: `v.ToString("0.##")`. */
const fmt = (v: number) => String(Math.round(v * 100) / 100);
const isChance = (v: number) => v >= 0 && v <= 1;
const n = (v: number | null | undefined, def: number) => (v == null ? def : v);

// Loose views of the sections the wind fields live on (the generated types predate them).
type Obj = Record<string, unknown>;
const o = (v: unknown): Obj | null => (v != null && typeof v === 'object' ? (v as Obj) : null);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const f = (x: Obj | null, k: string, def: number) => n(x?.[k] as number | null | undefined, def);
const s = (x: Obj | null, k: string, def: string) => {
  const v = x?.[k];
  return v == null ? def : String(v);
};

/** A bundle that plays wind (it has `raidRules.wind`). */
export function isWindBundle(b: Bundle): boolean {
  return o(o(b.raidRules)?.wind) != null;
}

/** `WindRules.Tiers`: the bundle's tiers, or the three defaults when it has none. */
function tierIds(b: Bundle): (string | null)[] {
  const rows = list((b as Obj).winds);
  if (rows.length > 0) return rows.map((r) => (o(r)?.id as string | null | undefined) ?? null);
  return [...WIND_TIER_IDS];
}

/** `ContentValidator.WindSections`: called after the health rules, like the server. */
export function validateWind(b: Bundle, err: Add) {
  const issues: Issue[] = [];
  const errors = new Sink(issues, 'raidRules', undefined, 'wind');
  const wind = isWindBundle(b);
  const tiers = list((b as Obj).winds);
  const raid = o(b.raidRules);
  if (wind) windRulesBlock(o(raid?.wind) as WindDef, errors);
  if (wind || tiers.length > 0) windTiers(tiers, errors.at('winds', undefined, ''));
  const known = tierIds(b);
  const tier = (id: string | null | undefined, where: string, sink?: Sink) => {
    if (!wind && (id == null || id === '')) return;
    // The section is the path's first segment (raidRules, squadRules, homeRules, lureSites, conditions) unless a sink is given.
    const dot = where.indexOf('.');
    const to = sink ?? errors.at(where.slice(0, dot), undefined, where.slice(dot + 1));
    if (id == null || id === '' || !known.includes(id))
      to.push(`${where} names unknown wind tier '${id ?? ''}'.`);
  };

  if (wind) {
    const w = o(raid?.wind);
    const rest = w?.rest === undefined ? {} : o(w?.rest);
    const st = w?.startTier === undefined ? {} : o(w?.startTier);
    tier(
      rest == null ? null : s(rest, 'startFloorTier', 'gassed'),
      'raidRules.wind.rest.startFloorTier',
    );
    tier(
      st == null ? null : s(st, 'v4ResumeTier', 'fresh'),
      'raidRules.wind.startTier.v4ResumeTier',
    );
    // An absent section or field reads as its C# default (every one of these defaults to an object); an explicit null reads null
    // on the server, an unknown tier.
    const sec = (parent: unknown, k: string): Obj | null => {
      const v = o(parent)?.[k];
      return v === undefined ? {} : o(v);
    };
    const at = (x: Obj | null, k: string, def: string) => (x == null ? null : s(x, k, def));
    const sq = sec(b, 'squadRules');
    const ai = sq == null ? null : sec(sq, 'ai');
    tier(at(sq, 'reviveTier', 'winded'), 'squadRules.reviveTier');
    tier(at(ai, 'fallBackAtTier', 'winded'), 'squadRules.ai.fallBackAtTier');
    tier(at(ai, 'clutchAtTier', 'gassed'), 'squadRules.ai.clutchAtTier');
    const home = sec(b, 'homeRules');
    tier(
      at(home == null ? null : sec(home, 'headline'), 'badlyHurtTier', 'gassed'),
      'homeRules.headline.badlyHurtTier',
    );
    tier(at(sec(b, 'lureSites'), 'strandedTier', 'winded'), 'lureSites.strandedTier');
    const c = o(b.conditions);
    if (c != null && c.enabled === true) {
      tier(s(c, 'woundedAtTier', 'gassed'), 'conditions.woundedAtTier');
      tier(s(c, 'laidUpAtTier', 'gassed'), 'conditions.laidUpAtTier');
      tier(s(c, 'playerTurnedMaxTier', 'winded'), 'conditions.playerTurnedMaxTier');
    }
    const families = o(o(b.effectRules)?.derivedFamilies);
    if (families == null || !('health' in families))
      errors
        .at('effectRules', undefined, 'derivedFamilies')
        .push("effectRules.derivedFamilies needs 'health' (the health level's wind, wind bundle).");
    list(o((b as Obj).rescues)?.scenarios).forEach((sc, si) => {
      const so = o(sc);
      for (const t of list(so?.trapped)) {
        const to = o(t);
        if (to != null)
          tier(
            s(to, 'startTier', 'winded'),
            `Rescue '${s(so, 'id', '')}' trapped '${s(to, 'slot', '')}' startTier`,
            errors.at('rescues', si, 'trapped.startTier'),
          );
      }
    });
  }

  windItems(b, wind, errors);
  windZombies(b, wind, errors);
  windBlocks(b, wind, errors);
  for (const i of issues) err(i.section, i.index, i.path, i.message);
}

function windRulesBlock(w: WindDef, errors: Sink) {
  const p = 'raidRules.wind.';
  const R = (v: number, lo: number, hi: number, name: string) => {
    if (Number.isNaN(v) || v < lo || v > hi)
      errors.push(`${p}${name} must be ${fmt(lo)}..${fmt(hi)}.`);
  };
  const capacity = n(w.capacity, 100);
  if (capacity < 40 || capacity > 300) errors.push(`${p}capacity must be 40..300.`);
  const nul = (k: keyof WindDef) => w[k] === null;
  if (
    nul('drop') ||
    nul('recovery') ||
    nul('sprint') ||
    nul('costs') ||
    nul('bite') ||
    nul('hits') ||
    nul('wounds') ||
    nul('rest') ||
    w.rest?.hoursPerWind === null ||
    nul('startTier')
  ) {
    errors.push(
      'raidRules.wind needs drop, recovery, sprint, costs, bite, hits, wounds, rest (with hoursPerWind) and startTier.',
    );
    return;
  }
  const rc = w.recovery ?? {};
  R(n(w.drop?.graceS, 0.5), 0, 3, 'drop.graceS');
  R(n(w.drop?.landFrac, 0.5), 0, 1, 'drop.landFrac');
  R(n(rc.pauseS, 1), 0, 5, 'recovery.pauseS');
  R(n(rc.pauseAfterDropS, 2.5), 0, 10, 'recovery.pauseAfterDropS');
  R(n(rc.fastPerS, 8), 0.1, 50, 'recovery.fastPerS');
  R(n(rc.fastUntilFrac, 0.7), 0, 1, 'recovery.fastUntilFrac');
  R(n(rc.slowPerS, 3), 0.1, 50, 'recovery.slowPerS');
  if (n(rc.slowPerS, 3) > n(rc.fastPerS, 8))
    errors.push(`${p}recovery.slowPerS must be at most fastPerS.`);
  R(n(rc.walkMult, 0.4), 0, 1, 'recovery.walkMult');
  R(n(rc.crouchMult, 0.6), 0, 1, 'recovery.crouchMult');
  R(n(rc.holdUnderThreatMult, 0.4), 0, 1, 'recovery.holdUnderThreatMult');
  R(n(w.sprint?.costPerS, 6), 0.1, 30, 'sprint.costPerS');
  R(n(w.sprint?.minToStart, 15), 0, capacity, 'sprint.minToStart');
  const c = w.costs ?? {};
  R(n(c.pushOff, 12), 0, 50, 'costs.pushOff');
  R(n(c.takedown, 6), 0, 50, 'costs.takedown');
  R(n(c.kick, 6), 0, 50, 'costs.kick');
  R(n(c.throw, 4), 0, 50, 'costs.throw');
  R(n(c.reload, 1), 0, 20, 'costs.reload');
  R(n(c.sustainedFireWindowS, 0.3), 0.05, 2, 'costs.sustainedFireWindowS');
  R(n(w.bite?.baseOdds, 0.04), 0, 1, 'bite.baseOdds');
  R(n(w.bite?.spentK, 1.5), 0, 10, 'bite.spentK');
  R(n(w.bite?.maxOdds, 0.9), 0, 1, 'bite.maxOdds');
  R(n(w.hits?.damageToWind, 1), 0, 5, 'hits.damageToWind');
  const toDie = n(w.wounds?.toDie, 3);
  const toDown = n(w.wounds?.toDown, 2);
  if (toDie < 1 || toDie > 10) errors.push(`${p}wounds.toDie must be 1..10.`);
  if (toDown < 1 || toDown > toDie) errors.push(`${p}wounds.toDown must be 1..toDie.`);
  const mults = w.wounds?.capacityMultByWounds ?? [];
  if (mults.length < 1) errors.push(`${p}wounds.capacityMultByWounds needs at least one value.`);
  mults.forEach((m, i) => {
    if (m < 0.1 || m > 1) errors.push(`${p}wounds.capacityMultByWounds values must be 0.1..1.`);
    if (i > 0 && m > mults[i - 1]!)
      errors.push(`${p}wounds.capacityMultByWounds must not increase.`);
  });
  const h = w.rest?.hoursPerWind ?? {};
  R(n(h.home, 2), 0.1, 48, 'rest.hoursPerWind.home');
  R(n(h.outpost, 3), 0.1, 48, 'rest.hoursPerWind.outpost');
  R(n(h.away, 4), 0.1, 48, 'rest.hoursPerWind.away');
  R(n(w.rest?.debtPerWound, 0.5), 0, 3, 'rest.debtPerWound');
  R(n(w.rest?.maxDebt, 3), 0, 10, 'rest.maxDebt');
}

function windTiers(tiers: unknown[], sink: Sink) {
  let errors = sink;
  if (tiers.length < 2) {
    errors.push('A wind bundle needs at least 2 winds[] rows (FRESH first).');
    return;
  }
  const ids = new Set<string>();
  tiers.forEach((raw, i) => {
    errors = sink.at('winds', i, '');
    const t = o(raw) as WindTierDef | null;
    if (t == null) {
      errors.push(`winds[${i}] is empty.`);
      return;
    }
    const id = t.id ?? '';
    const w = `Wind tier '${id}'`;
    if (id.trim() === '') errors.push(`winds[${i}] needs an id.`);
    else if (ids.has(id)) errors.push(`Duplicate wind tier id '${id}'.`);
    else ids.add(id);
    if ((t.name ?? '').trim() === '') errors.push(`${w} needs a name.`);
    const slot = t.colorSlot ?? '';
    if (slot.trim() === '') errors.push(`${w} needs a colorSlot (a palette slot, never a hex).`);
    else if (slot.startsWith('#')) errors.push(`${w} colorSlot must be a palette slot, not a hex.`);
    const cap = n(t.capacityMult, 1);
    const rec = n(t.recoveryMult, 1);
    if (cap < 0.05 || cap > 1) errors.push(`${w} needs capacityMult 0.05..1.`);
    if (rec < 0.05 || rec > 1) errors.push(`${w} needs recoveryMult 0.05..1.`);
    const bite = n(t.biteOddsMult, 1);
    if (bite < 1 || bite > 20) errors.push(`${w} needs biteOddsMult 1..20.`);
    const calm = n(t.climbBackCalmS, 0);
    if (calm < 0 || calm > 300) errors.push(`${w} needs climbBackCalmS 0..300.`);
    for (const [v, name] of [
      [n(t.swingIntervalMult, 1), 'swingIntervalMult'],
      [n(t.moveSpeedMult, 1), 'moveSpeedMult'],
      [n(t.acquireMult, 1), 'acquireMult'],
      [n(t.spreadMult, 1), 'spreadMult'],
    ] as const)
      if (v < 0.5 || v > 2) errors.push(`${w} needs ${name} 0.5..2.`);
    if (i === 0 && Math.abs(cap - 1) > 1e-6)
      errors.push(`${w} is the first tier: capacityMult must be 1.`);
    const prev = i > 0 ? (o(tiers[i - 1]) as WindTierDef | null) : null;
    if (prev != null) {
      if (cap > n(prev.capacityMult, 1))
        errors.push(`${w} capacityMult must not be above the tier before it.`);
      if (rec > n(prev.recoveryMult, 1))
        errors.push(`${w} recoveryMult must not be above the tier before it.`);
    }
  });
}

function windItems(b: Bundle, wind: boolean, sink: Sink) {
  let errors = sink;
  const weapon = (w: Obj, where: string) => {
    const cost = w.windCost as number | null | undefined;
    if (cost == null) {
      if (wind) errors.push(`${where} needs windCost (0..50).`);
    } else if (cost < 0 || cost > 50) errors.push(`${where} needs windCost 0..50.`);
    const perS = f(w, 'windCostPerS', 0);
    if (perS < 0 || perS > 20) errors.push(`${where} needs windCostPerS 0..20.`);
    const rec = f(w, 'recoveryMult', 1);
    if (rec < 0.3 || rec > 1.5) errors.push(`${where} needs recoveryMult 0.3..1.5.`);
  };
  const unarmed = o(o(b.raidRules)?.unarmed);
  errors = sink.at('raidRules', undefined, 'unarmed');
  if (unarmed != null) weapon(unarmed, 'raidRules.unarmed');
  (b.items ?? []).forEach((raw, index) => {
    const i = o(raw);
    if (i == null) return;
    const id = s(i, 'id', '');
    const w = o(i.weapon);
    errors = sink.at('items', index, 'weapon');
    if (w != null) weapon(w, `Weapon '${id}'`);
    const a = o(i.armor);
    errors = sink.at('items', index, 'armor');
    if (a != null) {
      const where = `Armor '${id}'`;
      const guard = a.windGuard as number | null | undefined;
      if (guard == null) {
        if (wind) errors.push(`${where} needs windGuard (0..200).`);
      } else if (guard < 0 || guard > 200) errors.push(`${where} needs windGuard 0..200.`);
      const cm = f(a, 'windCostMult', 1);
      if (cm < 0.5 || cm > 2) errors.push(`${where} needs windCostMult 0.5..2.`);
      const bm = f(a, 'biteOddsMult', 1);
      if (bm < 0.2 || bm > 1) errors.push(`${where} needs biteOddsMult 0.2..1.`);
      const blocks = f(a, 'blocksPerRaid', 0);
      if (wind && (blocks < 1 || blocks > 3))
        errors.push(`${where} needs blocksPerRaid 1..3 (wind).`);
    }
    const m = o(i.med);
    errors = sink.at('items', index, 'med');
    if (m != null) {
      const where = `Med '${id}'`;
      const R = (v: number, lo: number, hi: number, name: string) => {
        if (v < lo || v > hi) errors.push(`${where} needs ${name} ${fmt(lo)}..${fmt(hi)}.`);
      };
      R(f(m, 'wind', 0), 0, 200, 'wind');
      R(f(m, 'woundPenaltyClear', 0), 0, 3, 'woundPenaltyClear');
      R(f(m, 'climbTiers', 0), 0, 2, 'climbTiers');
      if (!isChance(f(m, 'climbBarFrac', 0))) errors.push(`${where} needs climbBarFrac 0..1.`);
      R(f(m, 'recoveryMult', 1), 1, 3, 'recoveryMult');
      R(f(m, 'recoveryS', 0), 0, 600, 'recoveryS');
      R(f(m, 'restWinds', 0), 0, 3, 'restWinds');
    } else if (wind && f(i, 'heal', 0) > 0)
      errors.push(`Item '${id}' heals (heal ${f(i, 'heal', 0)}) but has no med block (wind).`);
  });
}

function windZombies(b: Bundle, wind: boolean, sink: Sink) {
  (b.zombies ?? []).forEach((raw, index) => {
    const errors = sink.at('zombies', index, 'attack');
    const z = o(raw);
    if (z == null) return;
    const where = `Zombie '${s(z, 'id', '')}'`;
    const attack = z.attack as string | null | undefined;
    const hit = f(z, 'hitChance', 0);
    if (attack == null) {
      if (wind) errors.push(`${where} needs attack (grab, hit or mixed).`);
    } else if (!(ZOMBIE_ATTACK_KINDS as readonly string[]).includes(attack))
      errors.push(`${where} has unknown attack '${attack}' (grab, hit or mixed).`);
    else if (attack === 'mixed' && !(hit > 0 && hit <= 1))
      errors.push(`${where} is mixed: it needs hitChance above 0 and at most 1.`);
    if (!isChance(hit)) errors.push(`${where} needs hitChance 0..1.`);
    const pm = f(z, 'pushOffMult', 1);
    if (pm < 0.1 || pm > 5) errors.push(`${where} needs pushOffMult 0.1..5.`);
    const bm = f(z, 'biteOddsMult', 1);
    if (bm < 0.1 || bm > 5) errors.push(`${where} needs biteOddsMult 0.1..5.`);
    const tel = f(z, 'grabTelegraphS', 0.5);
    if (tel < 0.4 - 1e-4 || tel > 0.6 + 1e-4)
      errors.push(`${where} needs grabTelegraphS 0.4..0.6.`);
    if (list(z.attacks).length > 0)
      errors.push(
        `${where} attacks[] is reserved for a future ranged zombie: keep it empty in this build.`,
      );
  });
}

function windBlocks(b: Bundle, wind: boolean, sink: Sink) {
  let errors = sink.at('squadRules', undefined, '');
  const R = (v: number, lo: number, hi: number, what: string) => {
    if (Number.isNaN(v) || v < lo || v > hi) errors.push(`${what} must be ${fmt(lo)}..${fmt(hi)}.`);
  };
  const sq = o(b.squadRules);
  if (sq != null) {
    const d = f(sq, 'downedWoundsToDie', 1);
    if (d < 1 || d > 5) errors.push('squadRules.downedWoundsToDie must be 1..5.');
    R(f(sq, 'reviveWindFrac', 0.3), 0, 1, 'squadRules.reviveWindFrac');
    const ai = o(sq.ai);
    if (ai != null) R(f(ai, 'hurtWindowS', 10), 1, 60, 'squadRules.ai.hurtWindowS');
  }
  errors = sink.at('teamRules', undefined, 'fatigueTiers');
  for (const t of list(o(b.teamRules)?.fatigueTiers)) {
    const to = o(t);
    if (to != null)
      R(
        f(to, 'windCapacityMult', 1),
        0.1,
        1,
        `teamRules.fatigueTiers[${s(to, 'id', '')}].windCapacityMult`,
      );
  }
  list(b.survivorTraits).forEach((t, ti) => {
    errors = sink.at('survivorTraits', ti, 'windCapacityBonus');
    const to = o(t);
    const bonus = f(to, 'windCapacityBonus', 0);
    if (to != null && (bonus < 0 || bonus > 100))
      errors.push(`Survivor trait '${s(to, 'id', '')}' needs windCapacityBonus 0..100.`);
  });
  errors = sink.at('conditions', undefined, '');
  const c = o(b.conditions);
  if (c != null) {
    R(f(c, 'minWindCapacityMult', 0.5), 0.1, 1, 'conditions.minWindCapacityMult');
    R(f(c, 'biteChancePerGrab', 0), 0, 1, 'conditions.biteChancePerGrab');
    if (f(c, 'woundedAtWounds', 1) < 0 || f(c, 'laidUpAtWounds', 1) < 0)
      errors.push('conditions.woundedAtWounds and laidUpAtWounds must be 0 or more.');
    for (const d of list(c.types)) {
      const dd = o(d);
      if (dd == null) continue;
      R(
        f(dd, 'windCapacityMult', 1),
        0.1,
        1,
        `conditions.types[${s(dd, 'id', '')}].windCapacityMult`,
      );
      R(
        f(dd, 'windRecoveryMult', 1),
        0.1,
        1.5,
        `conditions.types[${s(dd, 'id', '')}].windRecoveryMult`,
      );
    }
  }
  (b.baseModules ?? []).forEach((m, mi) => {
    errors = sink.at('baseModules', mi, 'levels');
    const mo = o(m);
    for (const l of list(mo?.levels)) {
      const lo = o(l);
      const rm = f(lo, 'restMult', 0);
      if (lo != null && rm !== 0)
        R(rm, 0.1, 1, `Module '${s(mo, 'id', '')}' level ${f(lo, 'level', 0)} restMult`);
    }
  });
  errors = sink.at('homeRules', undefined, 'dischargeMarkMinRest');
  const home = o(b.homeRules);
  if (home != null && f(home, 'dischargeMarkMinRest', 1) < 0)
    errors.push('homeRules.dischargeMarkMinRest must be 0 or more.');
  errors = sink.at('healthRules', undefined, '');
  const h = o(b.healthRules);
  if (h != null) {
    const lmt = f(h, 'levelMinTier', 2);
    if (lmt < 0 || lmt > 3) errors.push('healthRules.levelMinTier must be 0..3.');
    const lw = o(h.levelWind);
    levelList(
      lw?.recovery as number[] | null | undefined,
      0.3,
      2,
      'healthRules.levelWind.recovery',
      errors,
    );
    levelList(
      lw?.rest as number[] | null | undefined,
      0.5,
      3,
      'healthRules.levelWind.rest',
      errors,
    );
  }
  errors = sink.at('survival', undefined, 'starvationDebtPerDay');
  const surv = o(b.survival);
  if (surv != null) R(f(surv, 'starvationDebtPerDay', 1), 0, 3, 'survival.starvationDebtPerDay');
  errors = sink.at('encounters', undefined, 'types');
  for (const e of list(o(b.encounters)?.types)) {
    const eo = o(e);
    if (eo != null) R(f(eo, 'fleeWind', 0), 0, 3, `Encounter '${s(eo, 'id', '')}' fleeWind`);
  }
  // Story effects: injure.wind is hit wind, boon.wind a restore; required in a wind bundle wherever the old field is set.
  list((b as Obj).beatChains).forEach((chain, ci) => {
    errors = sink.at('beatChains', ci, 'outcomes');
    const co = o(chain);
    for (const [key, outcome] of Object.entries(o(co?.outcomes) ?? {})) {
      const effects = list(o(outcome)?.effects) as Parameters<typeof flatten>[0];
      for (const e of flatten(effects)) {
        const eo = o(e);
        const where = `Beat chain '${s(co, 'id', '')}' outcome '${key}'`;
        const inj = o(eo?.injure);
        if (inj != null) {
          if (inj.wind != null) R(inj.wind as number, 0, 100, `${where} injure.wind`);
          else if (wind) errors.push(`${where} injure needs wind (0..100).`);
        }
        const boon = o(eo?.boon);
        if (boon != null) {
          if (boon.wind != null) R(boon.wind as number, 0, 100, `${where} boon.wind`);
          else if (wind && f(boon, 'heal', 0) > 0)
            errors.push(`${where} boon needs wind (0..100).`);
        }
      }
    }
  });
}

function levelList(
  v: number[] | null | undefined,
  lo: number,
  hi: number,
  where: string,
  errors: Sink,
) {
  if (v == null || v.length === 0) return;
  if (v.length !== 5) errors.push(`${where} needs 5 values (none, walker, mover, athlete, beast).`);
  v.forEach((x, i) => {
    if (x < lo || x > hi) errors.push(`${where} values must be ${fmt(lo)}..${fmt(hi)}.`);
    if (i > 0 && x < v[i - 1]!) errors.push(`${where} must not decrease.`);
  });
}

/** shared-cs `ModifierRegistry.Retired` (ADR 0073): registered for one content version, then removed. */
export const RETIRED_KEYS = ['body.maxHealth', 'body.sprintStamina', 'body.staminaRegen'] as const;
const retiredHint = (k: string) =>
  k === 'body.maxHealth'
    ? 'use body.windCapacity'
    : k === 'body.staminaRegen'
      ? 'use body.windRecovery'
      : 'use combat.windCost.sprint (1/value)';

/**
 * `ContentValidator.WindWarnings` (contract §3.8): in a wind bundle, an old HP field still in use, a retired key and a `grant_health`
 * trigger. Warnings only; they become errors at the contract step.
 */
export function windWarnings(b: Bundle, warn: Add) {
  if (!isWindBundle(b)) return;
  (b.items ?? []).forEach((raw, i) => {
    const it = o(raw);
    if (it == null) return;
    const id = s(it, 'id', '');
    if (f(o(it.armor), 'bonusHealth', 0) > 0)
      warn(
        'items',
        i,
        'armor.bonusHealth',
        `Armor '${id}' bonusHealth is retired (wind): windGuard replaces it.`,
      );
    if (f(it, 'heal', 0) > 0)
      warn('items', i, 'heal', `Item '${id}' heal is retired (wind): med replaces it.`);
  });
  (b.triggers ?? []).forEach((t, i) => {
    if (t != null && t.action === 'grant_health' && t.status !== 'retired')
      warn(
        'triggers',
        i,
        'action',
        `grant_health is retired (wind): trigger '${t.id ?? ''}' should use grant_wind.`,
      );
  });
  for (const key of usedKeys(b))
    if ((RETIRED_KEYS as readonly string[]).includes(key))
      warn(
        'effectRules',
        undefined,
        'keys',
        `Modifier key '${key}' is retired (wind): ${retiredHint(key)}.`,
      );
  for (const e of list(o(b.encounters)?.types)) {
    const eo = o(e);
    if (eo != null && f(eo, 'fleeHealth', 0) > 0)
      warn(
        'encounters',
        undefined,
        'types',
        `Encounter '${s(eo, 'id', '')}' fleeHealth is retired (wind): fleeWind replaces it.`,
      );
  }
}

/** Every modifier key the bundle names: statuses, governs, milestones and perks (distinct, in first-seen order). */
function usedKeys(b: Bundle): string[] {
  const seen: string[] = [];
  const k = (key: unknown) => {
    if (typeof key === 'string' && key !== '' && !seen.includes(key)) seen.push(key);
  };
  const mods = (m: unknown) => list(m).forEach((x) => k(o(x)?.key));
  for (const e of list(b.statusEffects)) mods(o(e)?.modifiers);
  for (const rows of [list(b.coreStats), list(b.skills)])
    for (const r of rows) {
      const ro = o(r);
      if (ro == null) continue;
      for (const g of list(ro.governs)) k(o(g)?.key);
      mods(o(ro.ability5)?.modifiers);
      mods(o(ro.signature10)?.modifiers);
    }
  for (const p of list(b.perks)) mods(o(p)?.modifiers);
  return seen;
}

/** `TriggerHealthTargets.All` is also the wind block's target list. */
export const TRIGGER_WIND_TARGETS = TRIGGER_HEALTH_TARGETS;
